import React, { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Container,
  CircularProgress,
  Slide,
  Grid,
} from "@mui/material";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { fetchLogin } from "../Hooks/Api";
import { TokenAtom, isLoginSelector } from "../Hooks/TokenAtom";
import { useSetRecoilState, useRecoilValue } from "recoil";
import MySnackbar from "../Hooks/MySnackBar";
import familyBackgroundImage from "../assets/familyalbum.png";
import photo1 from "../assets/photo1.jpg";
import photo2 from "../assets/photo2.jpg";
import photo3 from "../assets/photo7.jpg";
import photo5 from "../assets/photo6.jpg";

const LOGIN_MAX_WIDTH = "360px";
const SLIDE_SPEED = 400; // 슬라이드 속도

const modernGradientTheme = createTheme({
  typography: {
    fontFamily: ['"Roboto"', '"Noto Sans KR"', "sans-serif"].join(","),
    h5: { fontWeight: 700, color: "#333333" },
  },
  palette: {
    primary: { main: "#B8860B" },
    secondary: { main: "#F5DEB3" },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            "& fieldset": { borderColor: "#E0E0E0" },
            "&.Mui-focused fieldset": { borderColor: "#B8860B" },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 0",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          "&:hover": {
            backgroundColor: "#A0760A",
            boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
          },
        },
      },
    },
  },
});

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [openBox, setOpenBox] = useState(false);

  const isLogin = useRecoilValue(isLoginSelector);
  const setAccessToken = useSetRecoilState(TokenAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (isLogin) {
      navigate("/Home");
    }

    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");
    const savedRememberMe =
      localStorage.getItem("rememberMe") === "true" ? true : false;

    setRememberMe(savedRememberMe); // rememberMe 상태는 항상 초기화

    // rememberMe가 true일 때만 이메일/비밀번호 불러오기
    if (savedRememberMe) {
      if (savedEmail) setEmail(savedEmail);
      if (savedPassword) setPassword(savedPassword);
    }
  }, [isLogin, navigate]);

  const handleCloseSnackBar = () => setIsSnackBarOpen(false);
  const handleOpenBox = () => setOpenBox(true);
  const handleCloseBox = () => setOpenBox(false);

  const handleSubmitForm = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const data = await fetchLogin(
        email,
        password,
        localStorage.getItem("shootToken")
      );
      if (data.status === 200) {
        setLoading(false);
        setAccessToken({
          access: data.data.access,
          refresh: data.data.refresh,
        });
        handleCloseBox();
        navigate("/Home");

        if (rememberMe) {
          localStorage.setItem("email", email);
          localStorage.setItem("password", password);
          localStorage.setItem("rememberMe", rememberMe);
        } else {
          localStorage.removeItem("email");
          localStorage.removeItem("password");
          localStorage.removeItem("rememberMe");
        }
      } else {
        setLoading(false);
        setSnackBarMessage("로그인 실패: ID/PW 확인 또는 인터넷 연결 확인");
        setSnackBarSeverity("error");
        setIsSnackBarOpen(true);
      }
    } catch (error) {
      setLoading(false);
      setSnackBarMessage("네트워크 오류 발생. 잠시 후 다시 시도해주세요.");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  return (
    <ThemeProvider theme={modernGradientTheme}>
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          backgroundImage: `url(${familyBackgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 6,
          pb: 4,
          position: "relative",
        }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
            borderRadius: 3,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
            width: "100%",
            height: "75vh",
            maxWidth: LOGIN_MAX_WIDTH,
          }}
        >
          <Avatar
            sx={{
              m: 2,
              bgcolor: "secondary.main",
              width: 60,
              height: 60,
            }}
          >
            <CollectionsBookmarkOutlinedIcon
              sx={{
                color: modernGradientTheme.palette.primary.main,
                fontSize: 40,
              }}
            />
          </Avatar>

          <Typography
            component="h1"
            variant="h5"
            sx={{ mb: 3, width: "100%", textAlign: "center" }}
          >
            소중한 기록 <br />
            가족 앨범
          </Typography>

          <Box
            sx={{
              flex: 1,
              width: "100%",
              position: "relative",
              mt: 1,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={photo3}
              sx={{
                width: 110,
                height: 110,
                objectFit: "cover",
                borderRadius: 3,
                position: "absolute",
                top: "4%",
                left: "10%",
                transform: "rotate(-6deg)",
                boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                opacity: 0.85,
                zIndex: 2,
              }}
            />
            <Box
              component="img"
              src={photo2}
              sx={{
                width: 125,
                height: 125,
                objectFit: "cover",
                borderRadius: 3,
                position: "absolute",
                top: "0%",
                right: "10%",
                transform: "rotate(7deg)",
                boxShadow: "0 7px 15px rgba(0,0,0,0.18)",
                opacity: 0.85,
                zIndex: 2,
              }}
            />
            <Box
              component="img"
              src={photo1}
              sx={{
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: 3,
                position: "absolute",
                bottom: "10%",
                left: "15%",
                transform: "rotate(3deg)",
                boxShadow: "0 7px 16px rgba(0,0,0,0.20)",
                opacity: 0.85,
                zIndex: 2,
              }}
            />
            <Box
              component="img"
              src={photo5}
              sx={{
                width: 110,
                height: 110,
                objectFit: "cover",
                borderRadius: 3,
                position: "absolute",
                bottom: "14%",
                right: "14%",
                transform: "rotate(-4deg)",
                boxShadow: "0 6px 14px rgba(0,0,0,0.18)",
                opacity: 0.85,
                zIndex: 2,
              }}
            />
          </Box>

          {/* 시작하기 버튼 */}
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: "auto", mb: 2 }}
            onClick={handleOpenBox}
            color="primary"
          >
            시작하기
          </Button>

          {/* Slide 로그인 폼 */}
          <Slide
            direction="up"
            in={openBox}
            mountOnEnter
            unmountOnExit
            timeout={SLIDE_SPEED}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: 85, // 버튼 바로 위
                left: "5%",
                transform: "translateX(-50%)",
                width: "90%",
                maxWidth: LOGIN_MAX_WIDTH,
                // bgcolor: "#FFFCF9",
                borderRadius: 3,
                boxShadow: "0px 10px 30px rgba(0,0,0,0.2)",
                background: "linear-gradient(to bottom, #FFF8F0, #F5E1C9)", // 위→아래 그라데이션

                // backdropFilter: "blur(10px)", // 배경 흐림
                p: 4,
                zIndex: 999,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* <Typography
                component="h2"
                variant="h5"
                sx={{ mb: 4, fontWeight: 500, textAlign: "center" }}
              >
                가족 앨범에 오신 것을 환영합니다.
              </Typography> */}

              <Box
                component="form"
                onSubmit={handleSubmitForm}
                noValidate
                sx={{ mt: 0, width: "100%" }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="이메일 주소"
                  name="email"
                  autoComplete="email"
                  autoFocus={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="비밀번호"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      color="primary"
                      sx={{
                        transform: "scale(0.8)", // 체크박스 크기 축소
                      }}
                    />
                  }
                  label="로그인 정보 기억하기"
                  sx={{
                    mt: 1,
                    color: "#666666",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.85rem", // 라벨 글자 크기 축소
                    },
                  }}
                />

                <Grid container spacing={1} sx={{ mt: 4 }}>
                  <Grid item xs={8}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "로그인"
                      )}
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleCloseBox}
                    >
                      닫기
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Slide>
        </Box>
      </Container>

      <MySnackbar
        open={isSnackBarOpen}
        onClose={handleCloseSnackBar}
        message={snackBarMessage}
        severity={snackBarSeverity}
      />
    </ThemeProvider>
  );
}

export default Login;
